import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { parseInstagramUsername } from "@/lib/parseInstagramUsername";

type ChatMessage = {
  role: "bot" | "user";
  content: string;
  checkoutUrl?: string;
};

type OnboardingStep =
  | "awaiting_username"
  | "awaiting_email"
  | "awaiting_referral"
  | "complete";

const INTRO_MESSAGE =
  "Thanks for your interest in Engram. We are a small independent software company looking for our founding customers. Please answer a few questions to help us understand if we are a good fit for you.";
const USERNAME_QUESTION = "What is your instagram username?";
const USERNAME_RETRY =
  "Please enter only your Instagram username (for example: yourname).";
const EMAIL_QUESTION = "What is your email?";
const EMAIL_RETRY = "Please enter a valid email address.";
const REFERRAL_QUESTION =
  "Please enter your referral code. \n\n We are currently invite only. If you do not have a referral code we will reach out when spots open up";
const REFERRAL_RETRY =
  "That referral code is invalid. Please try again.";
const TRIAL_MESSAGE =
  "You've unlocked $11 off per month forever and a free 14 day trial. Please click the button below to start your free trial.";

const INITIAL_MESSAGES: ChatMessage[] = [
  { role: "bot", content: INTRO_MESSAGE },
  { role: "bot", content: USERNAME_QUESTION },
];

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function renderMessageContent(content: string) {
  const parts = content.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2"
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

function buildStateFromSavedAnswers({
  instagramUsername,
  email,
  referralCode,
  checkoutUrl,
}: {
  instagramUsername: string | null;
  email: string | null;
  referralCode: string | null;
  checkoutUrl: string | null;
}): {
  messages: ChatMessage[];
  step: OnboardingStep;
  instagramUsername: string | null;
  email: string | null;
} {
  const messages: ChatMessage[] = [
    { role: "bot", content: INTRO_MESSAGE },
    { role: "bot", content: USERNAME_QUESTION },
  ];

  if (!instagramUsername) {
    return {
      messages,
      step: "awaiting_username",
      instagramUsername: null,
      email: null,
    };
  }

  messages.push({ role: "user", content: instagramUsername });
  messages.push({ role: "bot", content: EMAIL_QUESTION });

  if (!email) {
    return {
      messages,
      step: "awaiting_email",
      instagramUsername,
      email: null,
    };
  }

  messages.push({ role: "user", content: email });
  messages.push({ role: "bot", content: REFERRAL_QUESTION });

  if (!referralCode || !checkoutUrl) {
    return {
      messages,
      step: "awaiting_referral",
      instagramUsername,
      email,
    };
  }

  messages.push({ role: "user", content: referralCode });
  messages.push({
    role: "bot",
    content: TRIAL_MESSAGE,
    checkoutUrl,
  });
  return {
    messages,
    step: "complete",
    instagramUsername,
    email,
  };
}

export function OnboardingRoute() {
  const { clearBreadcrumbs } = useBreadcrumb();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [step, setStep] = useState<OnboardingStep>("awaiting_username");
  const [instagramUsername, setInstagramUsername] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch("/api/users/onboarding");
        if (response.ok) {
          const data = (await response.json()) as {
            instagramUsername: string | null;
            email: string | null;
            referralCode: string | null;
            checkoutUrl: string | null;
          };
          const saved = buildStateFromSavedAnswers(data);
          setMessages(saved.messages);
          setStep(saved.step);
          setInstagramUsername(saved.instagramUsername);
          setEmail(saved.email);
        } else {
          setMessages(INITIAL_MESSAGES);
        }
      } catch (error) {
        console.error("Onboarding fetch error:", error);
        setMessages(INITIAL_MESSAGES);
      } finally {
        setIsInitialized(true);
      }
    })();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const saveOnboarding = async ({
    instagramUsername: username,
    email: emailValue,
    referralCode,
  }: {
    instagramUsername?: string;
    email?: string;
    referralCode?: string;
  }) => {
    const response = await fetch("/api/users/onboarding", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...(username !== undefined ? { instagramUsername: username } : {}),
        ...(emailValue !== undefined ? { email: emailValue } : {}),
        ...(referralCode !== undefined ? { referralCode } : {}),
      }),
    });

    const data = (await response.json()) as {
      message?: string;
      error?: string;
      checkoutUrl?: string | null;
    };

    if (!response.ok) {
      throw new Error(data.error || "Failed to save onboarding");
    }

    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || step === "complete" || isSubmitting) return;

    setInput("");

    if (step === "awaiting_username") {
      const username = parseInstagramUsername(trimmed);
      if (!username) {
        setMessages((prev) => [
          ...prev,
          { role: "user", content: trimmed },
          { role: "bot", content: USERNAME_RETRY },
        ]);
        return;
      }

      setMessages((prev) => [...prev, { role: "user", content: username }]);
      setIsSubmitting(true);

      try {
        await saveOnboarding({ instagramUsername: username });
        setInstagramUsername(username);
        setMessages((prev) => [
          ...prev,
          { role: "bot", content: EMAIL_QUESTION },
        ]);
        setStep("awaiting_email");
      } catch (error) {
        console.error("Onboarding username save error:", error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (step === "awaiting_email") {
      const normalizedEmail = trimmed.toLowerCase();
      if (!isValidEmail(normalizedEmail)) {
        setMessages((prev) => [
          ...prev,
          { role: "user", content: trimmed },
          { role: "bot", content: EMAIL_RETRY },
        ]);
        return;
      }

      setMessages((prev) => [
        ...prev,
        { role: "user", content: normalizedEmail },
      ]);
      setIsSubmitting(true);

      try {
        await saveOnboarding({ email: normalizedEmail });
        setEmail(normalizedEmail);
        setMessages((prev) => [
          ...prev,
          { role: "bot", content: REFERRAL_QUESTION },
        ]);
        setStep("awaiting_referral");
      } catch (error) {
        console.error("Onboarding email save error:", error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setIsSubmitting(true);

    try {
      const data = await saveOnboarding({ referralCode: trimmed });
      if (!data.checkoutUrl) {
        setMessages((prev) => [
          ...prev,
          { role: "bot", content: REFERRAL_RETRY },
        ]);
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          content: TRIAL_MESSAGE,
          checkoutUrl: data.checkoutUrl!,
        },
      ]);
      setStep("complete");
    } catch (error) {
      console.error("Onboarding submit error:", error);
      setMessages((prev) => [
        ...prev,
        { role: "bot", content: REFERRAL_RETRY },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputDisabled = step === "complete" || isSubmitting || !isInitialized;

  return (
    <div className="container mx-auto flex min-h-screen flex-col items-center px-4">
      <div className="flex w-full max-w-lg flex-1 flex-col justify-center gap-4">
        <div className="flex min-h-[28rem] flex-1 flex-col space-y-3 overflow-y-auto pt-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                {message.role === "bot" ? (
                  message.checkoutUrl ? (
                    <div className="space-y-3">
                      <p>{message.content}</p>
                      <Button asChild className="w-full">
                        <a href={message.checkoutUrl}>Start Trial</a>
                      </Button>
                    </div>
                  ) : (
                    renderMessageContent(message.content)
                  )
                ) : (
                  message.content
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSubmit(e);
              }
            }}
            placeholder={inputDisabled ? "Chat complete" : "Type your response..."}
            disabled={inputDisabled}
            rows={2}
            className="min-h-[2.5rem] resize-none"
          />
          <Button
            type="submit"
            size="icon"
            disabled={inputDisabled || !input.trim()}
            className="shrink-0 self-end"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
      <footer className="flex w-full items-center justify-center py-4 text-center">
        <a
          href="mailto:hello@engram.sh"
          className="text-xs font-medium text-primary underline underline-offset-4"
        >
          Contact Us
        </a>
      </footer>
    </div>
  );
}
